import { memo, useMemo } from 'react'
import styled from 'styled-components'
import { useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import TextButton from 'LuxKit/Button/TextButton'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import Image from 'components/Common/Image'
import Group from 'components/utils/Group'
import { INTL_DAY_MONTH_NAME_SHORT } from 'constants/dateFormats'
import { useIntlDateRangeFormatter } from 'lib/datetime/dateUtils'
import FlightMarketingCard, { FlightMarketingCardHero } from 'components/Flights/FlightMerchandising/FlightMarketingCard'
import { normalizeAirportCode } from 'lib/flights/popularFlightUtils'

const FallbackHero = styled(FlightMarketingCardHero)`
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  display: flex;
  align-items: center;
  justify-content: center;
`

interface Props {
  originCode: string
  originCityName: string
  destinationCode: string
  destinationCityName: string
  to: string
  price?: number
  departDate?: string
  returnDate?: string
  loading?: boolean
  imageId?: string
}

function PopularFlightCard(props: Props) {
  const { originCode, originCityName, destinationCode, destinationCityName, to, price, departDate, returnDate, loading, imageId } = props
  const intl = useIntl()
  const dateRangeFormatter = useIntlDateRangeFormatter()
  const image = useMemo(() => ({ id: imageId }), [imageId])

  const dateLabel = departDate && returnDate
    ? dateRangeFormatter(departDate, returnDate, INTL_DAY_MONTH_NAME_SHORT)
    : undefined

  const hero = useMemo(() => imageId ? (
    <FlightMarketingCardHero>
      <Image image={image} dpr={2} width={400} fit="center" aspectRatio="5:3" />
    </FlightMarketingCardHero>
  ) : (
    <FallbackHero aria-hidden="true">
      <LinePlaneDepartureIcon size="XL" colour="light-foreground-alt" />
    </FallbackHero>
  ), [image, imageId])

  const title = intl.formatMessage(
    {
      id: 'KcL8VPQLg21BK3t7erPI',
      defaultMessage: '{originCity} ({originCode}) to {destCity} ({destCode})',
      description: 'Route label on popular flight card showing origin and destination',
    },
    { originCity: originCityName, originCode: normalizeAirportCode(originCode), destCity: destinationCityName, destCode: normalizeAirportCode(destinationCode) },
  )
  const description = useMemo(() => dateLabel ? (
    <BodyText variant="medium" colour="light-foreground-alt">
      {dateLabel}
    </BodyText>
  ) : undefined, [dateLabel])

  return (
    <FlightMarketingCard to={to} hero={hero} title={title} description={description}>
      <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
        <div>
          {loading && <TextLoadingBox width="12ch" />}
          {!loading && price != null && (
            <>
              <PriceRowPriceCaption>
                {intl.formatMessage({
                  id: 'G65No1IDdYZKznwDZoHW',
                  defaultMessage: 'Economy return from',
                  description: 'Price caption on popular flight card indicating 7 day return price',
                })}
              </PriceRowPriceCaption>
              <PriceRowPrice
                price={price}
                saleUnit="person"
                size="M"
              />
            </>
          )}
        </div>
        <TextButton kind="secondary" nonInteractable>
          {intl.formatMessage({
            id: '8jNUneFhRPtk7jqe6V0r',
            defaultMessage: 'Search',
            description: 'Button text to search for popular flight in the flights flow',
          })}
        </TextButton>
      </Group>
    </FlightMarketingCard>
  )
}

export default memo(PopularFlightCard)
